import { useCallback, useMemo } from "react";

import { useGatesConsumer } from "./GatesContext";
import gatesHandler from "./handler";

export default function useGates({ ...extra } = {}) {
  const { user, policies, defaultHandler, additional } = useGatesConsumer();

  const extraData = useMemo(() => {
    return {
      ...additional,
      ...extra,
    };
  }, [additional, extra]);

  const can = useCallback(
    /**
     * @param {string} permissions
     * @param {object} more
     * @returns {boolean} whether the user has the permission or not
     */
    (permission, more = {}) => {
      return gatesHandler(permission, user, policies, defaultHandler, { ...extraData, ...more });
    },
    [defaultHandler, extraData, policies, user]
  );

  const cannot = useCallback(
    /**
     * @param {string} permissions
     * @param {object} more
     * @returns {boolean} whether the user hasn't the permission or not
     */
    (permission, more = {}) => {
      return !can(permission, more);
    },
    [can]
  );

  const canAny = useCallback(
    /**
     * @param {string[]} permissions
     * @param {object} more
     * @returns {boolean} whether the user has some of the permissions or not
     */
    (permissions = [], more = {}) => {
      return permissions
        .map((permission) => gatesHandler(permission, user, policies, defaultHandler, { ...extraData, ...more }))
        .some((gateOutput) => gateOutput);
    },
    [defaultHandler, extraData, policies, user]
  );

  const canAll = useCallback(
    /**
     * @param {string[]} permissions
     * @param {object} more
     * @returns {boolean} whether the user has all the permissions or not
     */
    (permissions = [], more = {}) => {
      return permissions
        .map((permission) => gatesHandler(permission, user, policies, defaultHandler, { ...extraData, ...more }))
        .every((gateOutput) => gateOutput);
    },
    [defaultHandler, extraData, policies, user]
  );

  return {
    can,
    cannot,
    canAny,
    canAll,
  };
}
